import { useState, useEffect } from 'react';
import { userOnboardingService } from '@/services/userOnboardingService';
import { userFacingErrorMessage } from '@/utils/userFacingError';
import { translateOutsideReact } from '@/i18n/translateOutsideReact';

export interface UserOnboardingStatus {
  personalNumberCompleted: boolean;
  isComplete: boolean;
}

export function useUserOnboardingStatus() {
  const [status, setStatus] = useState<UserOnboardingStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    checkUserOnboardingStatus();
  }, []);

  const checkUserOnboardingStatus = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await userOnboardingService.getStatus();
      
      if (response.success) {
        const status: UserOnboardingStatus = {
          personalNumberCompleted: response.personalNumberCompleted,
          isComplete: response.isComplete
        };
        setStatus(status);
      } else {
        setError(response.error || translateOutsideReact('consultantErrors.fallback.userOnboardingStatusCheck'));
        setStatus({
          personalNumberCompleted: false,
          isComplete: false
        });
      }
    } catch (err) {
      setError(userFacingErrorMessage(err, translateOutsideReact('consultantErrors.fallback.userOnboardingStatusCheck')));
      setStatus({
        personalNumberCompleted: false,
        isComplete: false
      });
    } finally {
      setIsLoading(false);
    }
  };

  const refreshStatus = () => {
    checkUserOnboardingStatus();
  };

  return {
    status,
    isLoading,
    error,
    refreshStatus,
    needsUserOnboarding: isLoading ? false : (status ? !status.isComplete : true),
    isUserOnboardingComplete: status ? status.isComplete : false,
    needsPersonalNumber: status ? !status.personalNumberCompleted : true
  };
}